import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Divider} from 'views/components';
import {Translate} from 'views/module';
import {CheckCircle, ErrorOutline} from 'views/icons';

const AGREEMENT_KEYS = [
  'termsAgreement',
  'certifiesTruthfulness',
  'understandsPrivacyAndAgreesToConfidentialityPolicies',
  'certifiesValidAndUpToDateCertifications',
  'understandsAbuseProhibition',
  'committedToCodeOfConductAndEthics',
];

const toPascalCase = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const sanitizeAgreementValues = (source) => {
  return AGREEMENT_KEYS.reduce((accumulator, key) => {
    accumulator[key] = Boolean(source?.[key]);
    return accumulator;
  }, {});
};

const AgreementView = (props) => {
  const {bioQuestions, setIsComplete} = props;

  const agreements = React.useMemo(() => sanitizeAgreementValues(bioQuestions), [bioQuestions]);

  React.useEffect(() => {
    const isComplete = AGREEMENT_KEYS.every((key) => agreements[key]);
    setIsComplete(isComplete);
  }, [agreements, setIsComplete]);

  const allFalse = AGREEMENT_KEYS.every((key) => !agreements[key]);

  if (allFalse) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          <Translate expression="caregiver.CaregiverBiography.Agreement.EmptyState" />
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.75} alignItems="stretch" sx={{width: '100%'}}>
      <Typography variant="subtitle1" sx={{fontWeight: 600}}>
        <Translate expression="caregiver.CaregiverBiography.Agreement.Title" />
      </Typography>
      <Divider />
      {
        AGREEMENT_KEYS.map((key) => {
          const translationKey = toPascalCase(key);
          const isConfirmed = agreements[key];
          const statusExpression = isConfirmed ?
            'caregiver.CaregiverBiography.Agreement.Summary.Confirmed' :
            'caregiver.CaregiverBiography.Agreement.Summary.Pending';

          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
                border: '1px solid',
                borderColor: isConfirmed ? '#22c55e' : '#ea580c',
                backgroundColor: isConfirmed ? 'rgba(34,197,94,0.08)' : 'rgba(234,88,12,0.08)',
                borderRadius: '12px',
                paddingY: 1.25,
                paddingX: 1.75,
              }}
            >
              {
                isConfirmed ?
                  <CheckCircle sx={{color: '#16a34a', mt: 0.25}} fontSize="small" /> :
                  <ErrorOutline sx={{color: '#ea580c', mt: 0.25}} fontSize="small" />
              }
              <Stack spacing={0.5} flex={1}>
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                  <Translate expression={`caregiver.CaregiverBiography.Agreement.Questions.${translationKey}.Title`} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression={`caregiver.CaregiverBiography.Agreement.Questions.${translationKey}.Subtitle`} />
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isConfirmed ? '#15803d' : '#c2410c',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  <Translate expression={statusExpression} />
                </Typography>
              </Stack>
            </Box>
          );
        })
      }
    </Stack>
  );
};

AgreementView.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  setIsComplete: PropTypes.func.isRequired,
};

export default React.memo(AgreementView);
